import { Collapse, Typography } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Grid,
} from '@mui/material';
import { DocumentPortalScanElementLegalRequirements } from '../DocumentPortalScanElementLegalRequirements';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { DocumentOverview } from '../../Document/DocumentOverview';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { type ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';
import { ILegalTransactionTransactionNumber } from '../../Interfaces/ILegalTransactionTransactionNumber';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IFSSelectObject } from '../../Interfaces/IFSDirectory';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { InvoiceTab } from '../../LegalTransaction/InvoiceTab/InvoiceTab';
import { useGetLegalTransactionTypes } from '../../hooks/data';

type IProps = {
  recordNumbers: ILegalTransactionRecordNumber[];
  transactionNumbers: ILegalTransactionTransactionNumber[];
  selectObject: IFSSelectObject;
  setSelectObject: Dispatch<SetStateAction<IFSSelectObject>>;
  legalReqStates: ILegalRequirementState[];
  isLoadingCurrentLegalTransaction: boolean;
  currentLegalTransaction: ILegalTransactionFullObject | null;
  setCurrentLegalTransaction: Dispatch<
    SetStateAction<ILegalTransactionFullObject | null>
  >;
};

export const LegalTransactionSelect = ({
  recordNumbers,
  transactionNumbers,
  selectObject,
  setSelectObject,
  legalReqStates,
  isLoadingCurrentLegalTransaction,
  currentLegalTransaction,
  setCurrentLegalTransaction,
}: IProps) => {
  const { legalTransactionTypes, isLoadingLegalTransactionTypes } =
    useGetLegalTransactionTypes();
  const documentDirectories = useMemo(() => {
    if (!currentLegalTransaction) {
      return [];
    }

    const allDocumentDirectories: IDocumentDirectory[] = [
      {
        Title: 'Neuen Ordner anlegen',
        Created_at: null,
        idDocumentDirectory: -1,
        idLegalTransaction: currentLegalTransaction.idLegalTransaction,
        Parent_idDocumentDirectory: null,
      },
      ...currentLegalTransaction.DocumentDirectoryArray,
    ];

    return allDocumentDirectories;
  }, [currentLegalTransaction]);

  const concatenateParent = (currentDirectory: IDocumentDirectory): string => {
    if (currentLegalTransaction !== null) {
      if (currentDirectory.Parent_idDocumentDirectory !== null) {
        const testObject = currentLegalTransaction.DocumentDirectoryArray.find(
          documentDirectory =>
            documentDirectory.idDocumentDirectory ===
            currentDirectory.Parent_idDocumentDirectory
        );

        if (testObject) {
          return (
            concatenateParent(testObject) + ' \\ ' + currentDirectory.Title
          );
        }
      }
    }

    return currentDirectory.Title;
  };

  if (isLoadingCurrentLegalTransaction || isLoadingLegalTransactionTypes) {
    return (
      <Card variant='outlined' sx={{ mt: 2 }}>
        <LoadingSpinner height='30vh' />
      </Card>
    );
  }

  if (!currentLegalTransaction) {
    return (
      <Card variant='outlined' sx={{ mt: 2, height: '30vh' }}>
        <CardContent>
          <Typography>Keine Maßnahme ausgewählt</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mt: 2 }} variant='outlined'>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Bezeichnung'
                variant='standard'
                value={currentLegalTransaction.Title}
                size='small'
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label='Phase'
                variant='standard'
                value={currentLegalTransaction.LegalTransactionProgress}
                size='small'
                fullWidth
                InputProps={{}}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label='Status'
                variant='standard'
                value={currentLegalTransaction.LegalTransactionState}
                size='small'
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              {transactionNumbers.filter(
                transactionNumber =>
                  transactionNumber.idLegalTransaction ===
                  currentLegalTransaction.idLegalTransaction
              ).length === 0 ? (
                <TextField
                  label='Vorgangsnummer'
                  variant='standard'
                  size='small'
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              ) : (
                transactionNumbers
                  .filter(
                    transactionNumber =>
                      transactionNumber.idLegalTransaction ===
                      currentLegalTransaction.idLegalTransaction
                  )
                  .map(transactionNumber => (
                    <TextField
                      label='Vorgangsnummer'
                      variant='standard'
                      value={transactionNumber.TransactionNumberEntry}
                      size='small'
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  ))
              )}
            </Grid>
            <Grid item xs={3}>
              {recordNumbers.filter(
                recordNumber =>
                  recordNumber.idLegalTransaction ===
                  currentLegalTransaction.idLegalTransaction
              ).length === 0 ? (
                <TextField
                  label='Urkundennummer'
                  variant='standard'
                  size='small'
                  fullWidth
                  InputProps={{
                    readOnly: true,
                  }}
                />
              ) : (
                recordNumbers
                  .filter(
                    recordNumber =>
                      recordNumber.idLegalTransaction ===
                      currentLegalTransaction.idLegalTransaction
                  )
                  .map(recordNumber => (
                    <TextField
                      label='Urkundennummer'
                      variant='standard'
                      value={recordNumber.RecordNumberEntry}
                      size='small'
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  ))
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box mt={2} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            variant='outlined'
            label='Sektion'
            fullWidth
            value={selectObject.section}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSelectObject({
                ...selectObject,
                section: event.target.value,
                idDocumentDirectory: -1,
                idLegalRequirement: -1,
              });
            }}
            select
            size='small'
          >
            <MenuItem
              key={`fs-section-clegalRequirements`}
              value='legalRequirements'
            >
              Abwicklung / Maßnahmen
            </MenuItem>
            <MenuItem key={`fs-section-certificate`} value='certificate'>
              Urkunden
            </MenuItem>
            <MenuItem key={`fs-section-invoice`} value='invoice'>
              Rechnungen
            </MenuItem>
            <MenuItem key={`fs-section-dms`} value='dms'>
              Interne Datenablage
            </MenuItem>
          </TextField>
        </Grid>

        <DocumentPortalScanElementLegalRequirements
          currentLegalTransaction={currentLegalTransaction}
          setCurrentLegalTransaction={setCurrentLegalTransaction}
          selectObject={selectObject}
          setSelectObject={setSelectObject}
          legalReqStates={legalReqStates}
        />

        <Grid item xs={12}>
          <Collapse in={selectObject.section === 'certificate'} sx={{ mt: -2 }}>
            <DocumentOverview
              key='certification'
              allowRemove={false}
              allowUpload={false}
              title='Urkunden'
              documentArray={currentLegalTransaction.CertificateArray}
              setDocumentArray={() => {}}
              downloadedArray={[]}
            />
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <Collapse in={selectObject.section === 'invoice'} sx={{ mt: -4 }}>
            <InvoiceTab
              legalTransaction={currentLegalTransaction}
              documentArray={currentLegalTransaction.InvoiceArray}
              setDocumentArray={() => {}}
              invoiceObjectArray={currentLegalTransaction.InvoiceObjectArray}
              setInvoiceObjectArray={() => {}}
              assignedLegalClients={currentLegalTransaction.LegalClientArray}
              setLegalClients={() => {}}
              legalRoles={[]} // wenn isDisabledAddDocs = True -> nicht benötigt
              legalTransactionTypes={legalTransactionTypes ?? []}
              hasDirInvoice={false}
              recordNumberArray={[]}
              transactionNumberArray={[]}
              isDisableAddDocs={true}
            />
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <Collapse in={selectObject.section === 'dms'} sx={{ mt: -6 }}>
            <CustomeTextField
              attr='idDocumentDirectory'
              label='Ordner'
              object={selectObject}
              setObject={setSelectObject}
              type='select'
            >
              {documentDirectories.map(documentDirectory => (
                <MenuItem
                  key={`fs-dir-${documentDirectory.idDocumentDirectory}`}
                  value={documentDirectory.idDocumentDirectory}
                >
                  {concatenateParent(documentDirectory)}
                </MenuItem>
              ))}
            </CustomeTextField>
            <Box sx={{ mt: 2 }} />
            <DocumentOverview
              key='dms-ducmentenview'
              allowRemove={false}
              allowUpload={false}
              title={
                selectObject.idDocumentDirectory < 0
                  ? 'Neuen Ordner anlegen'
                  : String(
                      currentLegalTransaction.DocumentDirectoryArray.find(
                        documentDirectory =>
                          documentDirectory.idDocumentDirectory ===
                          selectObject.idDocumentDirectory
                      )?.Title
                    )
              }
              documentArray={currentLegalTransaction.DocumentArray.filter(
                document =>
                  document.idDocumentDirectory ===
                  selectObject.idDocumentDirectory
              )}
              setDocumentArray={() => {}}
              downloadedArray={[]}
            />
          </Collapse>
        </Grid>
      </Grid>
    </Box>
  );
};
